import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from './AuthContext';
import NotificationToast from '../components/ui/NotificationToast';

interface Notification {
    id: string;
    type: 'new_session';
    message: string;
    senderMatches?: boolean; // If true, this came from our partner
    data?: any; // The full session object
}

interface NotificationsContextType {
    notifications: Notification[];
    dismissNotification: (id: string) => void;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
    const { user } = useAuth();
    const [notifications, setNotifications] = useState<Notification[]>([]);


    useEffect(() => {
        if (!user) return;



        // Subscribe to changes in the 'sessions' table
        const channel = supabase
            .channel('public:sessions')
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'sessions',
                },
                async (payload) => {
                    const newSession = payload.new as any;

                    // 1. Ignore our own sessions
                    if (newSession.user_id === user.id) return;

                    // 2. Determine if it's from our partner (simplification: assume any other insert is relevant for now, 
                    // or ideally check user_links table. For performance, we can just fetch the user name).

                    // Fetch details of the product to make the message nice
                    const { data: productData } = await supabase
                        .from('products')
                        .select('name, strain:strains(name)')
                        .eq('id', newSession.product_id)
                        .single();

                    const productName = productData?.name || 'Unknown Product';
                    // @ts-ignore
                    const strainName = productData?.strain?.name || '';

                    const message = `Partner logged a session: ${productName} ${strainName ? `(${strainName})` : ''}`;

                    const newNotification: Notification = {
                        id: crypto.randomUUID(),
                        type: 'new_session',
                        message,
                        senderMatches: true,
                        data: {
                            ...newSession,
                            productName,
                            strainName
                        }
                    };

                    setNotifications((prev) => [newNotification, ...prev]);

                    // Optional: Play a sound
                    try {
                        const audio = new Audio('/notification.mp3'); // We'll need to add this asset or remove this line
                        audio.play().catch(e => console.log('Audio play failed (user interaction required first)', e));
                    } catch (e) {
                        // Ignore audio errors
                    }
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [user]);

    const dismissNotification = (id: string) => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
    };

    return (
        <NotificationsContext.Provider value={{ notifications, dismissNotification }}>
            {children}

            {/* Toast Container */}
            <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
                {notifications.map((notification) => (
                    <div key={notification.id} className="pointer-events-auto animate-in slide-in-from-right-full duration-300">
                        <NotificationToast
                            notification={notification}
                            onDismiss={() => dismissNotification(notification.id)}
                        />
                    </div>
                ))}
            </div>
        </NotificationsContext.Provider>
    );
}

export function useNotifications() {
    const context = useContext(NotificationsContext);
    if (context === undefined) {
        throw new Error('useNotifications must be used within a NotificationsProvider');
    }
    return context;
}
